/**
 * Copyright 2021 AEKI <admin@aeki.dev>
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import SANITIZER from './sanitizers'
const VALIDATOR = 'validator'
export default VALIDATOR

/**
 * Default value to return when validator is passed
 */
export const DEFAULT_PASS_VALUE = undefined

export class Schema {
  schema: (locale: any) => any
  locale: any
  language: string
  success: boolean

  constructor(schema: any) {
    this.schema = schema
    this.locale = {}
    this.language = 'default'
    this.success = true
  }

  validate(value: any) {
    this.success = true
    return {
      body: validate(value, this.schema(this.locale[this.language]), this.setSuccess),
      success: this.success,
    }
  }

  validateOne(value: any) {
    const schema = this.schema(this.locale[this.language])

    if (!Object.keys(value).every(key => Object.keys(schema).includes(key))) {
      throw Error('Invalid Field')
    }

    const intersection: (obj1: {}, obj2: {}) => string[] = (obj1, obj2) => {
      return Object.keys(obj1).filter({}.hasOwnProperty.bind(obj2))
    }

    const intersectedKeys = intersection(schema, value)

    const formattedValue: any = {},
      formattedSchema: any = {}

    intersectedKeys.map(key => {
      formattedValue[key] = value[key]
      formattedSchema[key] = schema[key]
    })

    return {
      body: validate(formattedValue, formattedSchema, this.setSuccess),
      success: this.success,
    }
  }

  setSuccess = (success: boolean) => {
    this.success = success
  }

  setLanguage(language: string) {
    this.language = language
  }

  setLocale(locale: { [key: string]: {} }) {
    this.locale = locale
  }

  getErrorMessages() {
    const currentSchema = this.schema(this.locale[this.language])

    const restructErrorMessages = function (schema: any) {
      const errorMessage: { [key: string]: any } = {}

      for (const key in schema) {
        if (Array.isArray(schema[key])) {
          errorMessage[key] = {}

          for (let i = 0; i < schema[key].length; i++) {
            const cb = schema[key][i]

            if (cb.name === array.name || cb.name === object.name) {
              if (cb.name === object.name) {
                errorMessage[key][cb.name] = restructErrorMessages(cb.schema)
              } else {
                errorMessage[key][cb.name] = restructErrorMessages(cb).schema
              }
            } else {
              errorMessage[key][cb.name] = cb.error
            }
          }
        }
      }

      return errorMessage
    }

    return restructErrorMessages(currentSchema)
  }
}

/**
 * Runs all validation callbacks on value and updates pair with corresponding error.
 * If value has different key from schema, exception is thrown.
 *
 * @param error Error message to return when validation fails
 * @param schema An object of validation callbacks
 */
export const validate = (
  value: any = {},
  schema: any,
  setSuccess?: (flag: boolean) => void,
): Promise<any> => {
  for (const key in schema) {
    const valueKeys = Object.keys(value)
    const schemaKeys = Object.keys(schema)
    const keyDiff = valueKeys.filter((value: string) => schemaKeys.indexOf(value) < 0)

    if (keyDiff.length > 0) {
      // given value has different schema
      throw Error('Invalid Schema')
    }

    let error
    if (Object.prototype.toString.call(schema[key]) === '[object Object]') {
      // object, recurse
      error = validate(value[key], schema[key], setSuccess)

      if (Object.keys(error).length > 0) {
        value[key] = error
      } else {
        delete value[key]
      }
    } else {
      // simply loop through schema array
      for (let i = 0; i < schema[key].length; i++) {
        const callback = schema[key][i]

        if (callback.type === SANITIZER) {
          // schema callback is a sanitizer, modify value
          value[key] = callback(value[key])
        } else {
          // assign error
          error = callback(value[key])
        }

        // Must stop at first error or error validator runs on the error not actual value
        if (error) {
          if (setSuccess) {
            setSuccess(false)
          }
          if (callback.name === array.name) {
            // Differentiate errors for array itself and errors for children
            if (error === true) {
              throw Error('Invalid Schema')
            } else {
              value[key] = error
            }
          } else {
            value[key] = { error, value: value[key] }
          }

          break
        }
      }
    }
  }

  return value
}

/**
 * Check if given schema is a valid object.
 * If value has different key from schema, exception is thrown.
 *
 * @param schema An object of validation callbacks
 */
export function object(schema: any) {
  const object = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (Object.prototype.toString.call(value) !== '[object Object]') {
      throw Error('Invalid Schema')
    }

    const error = validate(value, schema)
    if (Object.keys(error).length > 0) {
      return error
    }

    return false
  }

  object.type = VALIDATOR
  object.schema = schema
  return object
}

/**
 * Check if given schema is a valid array.
 * `This is not a type check!`
 *
 * @param schema An array of validation callbacks
 */
export function array(schema?: any) {
  const array = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (Array.isArray(value)) {
      // No need to validate empty value
      if (value.length === 0) {
        return DEFAULT_PASS_VALUE
      }

      if (schema) {
        const output: any = []
        let hasError = false

        for (let i = 0; i < value.length; i++) {
          for (let j = 0; j < schema.length; j++) {
            const error = schema[j](value[i])

            if (schema[j].name === object.name) {
              // object schema
              if (error) {
                output.push(error)
                hasError = true
              } else {
                output.push(value[i]) // leave value without error as it is
              }
            } else {
              // primitive schema
              if (error) {
                output.push({
                  error: error,
                  value: value[i],
                })
                hasError = true
              } else {
                output.push(value[i]) // leave value without error as it is
              }
            }
          }
        }

        if (hasError) return output
      }
    }

    return DEFAULT_PASS_VALUE
  }

  array.type = VALIDATOR
  array.schema = schema

  return array
}

/**
 * Check if value is a string
 *
 * @param error message or boolean to return after validation
 * @return callback for validation
 * @type validator
 */
export const string = (error: string | boolean = true) => {
  const string = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (Object.prototype.toString.call(value) === '[object String]') {
      return DEFAULT_PASS_VALUE
    }
    return string.error
  }

  string.type = VALIDATOR
  string.error = error

  return string
}

export const any = (error: string | boolean = true) => {
  const any = (value: any) => {
    return DEFAULT_PASS_VALUE
  }

  any.type = VALIDATOR
  any.error = error

  return string
}

/**
 * Check if value matches given pattern
 *
 * @param error message or boolean to return after validation
 * @param regex pattern
 * @return callback for validation
 * @type validator
 */
export const match = (error: string | boolean = true, pattern: string, flags?: string) => {
  const regex = new RegExp(pattern, flags || '')

  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const match = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (regex.test(value)) {
      return DEFAULT_PASS_VALUE
    }
    return match.error
  }

  match.type = VALIDATOR
  match.error = error

  return match
}

/**
 * Check if value is an email
 *
 * @param error message or boolean to return after validation
 * @parent `match`
 * @return callback for validation
 * @type validator
 */
export const email = (error: string | boolean = true) => {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const email = match(error, '^[A-Z0-9\'._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}$', 'i')

  return email
}

/**
 * Check if value is a mongo id
 *
 * @param error message or boolean to return after validation
 * @parent `match`
 * @return callback for validation
 * @type validator
 */
export const mongodbId = (error: string | boolean = true) => {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const mongodbId = match(error, '^[0-9a-fA-F]{24}$')

  return mongodbId
}

/**
 * Check if value is a phone number
 *
 * @param error message or boolean to return after validation
 * @parent `match`
 * @return callback for validation
 * @type validator
 */
export const phone = (error: string | boolean = true) => {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const phone = match(error, '^[0-9a-fA-F]{24}$')

  return phone
}

/**
 * Check if value is a number or not
 *
 * @param error message or boolean to return after validation
 * @return callback for validation
 * @type validator
 */
export const number = (error: string | boolean = true) => {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  function number(value: any) {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (parseInt(`${value}`) % 1 === 0) {
      return DEFAULT_PASS_VALUE
    }

    return error
  }

  number.type = VALIDATOR
  number.error = error

  return number
}

/**
 * Check if value is set or not
 *
 * @param error message or boolean to return after validation
 * @return callback for validation
 * @type validator
 */
export function required(error: string | boolean = true) {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const required = (value: any) =>
    value !== undefined && value !== '' ? DEFAULT_PASS_VALUE : required.error

  required.type = VALIDATOR
  required.error = error

  return required
}

/**
 * Check if value is exceeding given number or not
 *
 * @param error message or boolean to return after validation
 * @param length number of maximum value
 * @return callback for validation
 * @type validator
 */
export function asyncCheck(error: string | boolean = true, url: string) {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */

  const asyncCheck = async (value: any) => {
    const _url = url.replace('SEARCH_TERM', value)

    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else {
      const response = await fetch(_url)

      const responseJson = await response.json()

      if (responseJson) {
        return asyncCheck.error
      } else {
        return DEFAULT_PASS_VALUE
      }
    }
  }

  asyncCheck.type = VALIDATOR
  asyncCheck.error = error

  return asyncCheck
}

/**
 * Check if value is exceeding given number or not
 *
 * @param error message or boolean to return after validation
 * @param length number of maximum value
 * @return callback for validation
 * @type validator
 */
export function max(error: string | boolean = true, length: number) {
  const _length = Number(length)
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */

  const max = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (Array.isArray(value) || !string()(value)) {
      if (value.length <= _length) {
        return DEFAULT_PASS_VALUE
      }
    } else if (number()(value)) {
      if (value <= _length) {
        return DEFAULT_PASS_VALUE
      }
    }

    return max.error
  }

  max.type = VALIDATOR
  max.error = error

  return max
}

/**
 * Check if value is below given number or not
 *
 * @param error message or boolean to return after validation
 * @param length number of minimum value
 * @return callback for validation
 * @type validator
 */
export function min(error: string | boolean = true, length: number) {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const min = (value: any) => {
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (Array.isArray(value) || !string()(value)) {
      if (value.length >= length) {
        return DEFAULT_PASS_VALUE
      }
    } else if (number()(value)) {
      if (value >= length) {
        return DEFAULT_PASS_VALUE
      }
    }

    return min.error
  }

  min.type = VALIDATOR
  min.error = error

  return min
}

/**
 * Check if value is valid date or not
 *
 * @param error message or boolean to return after validation
 * @param length number of minimum value
 * @return callback for validation
 * @type validator
 */
export const date = (error: string | boolean = true) => {
  /**
   * @param value to validate
   * @return `DEFAULT_PASS_VALUE` or error
   */
  const date = (value: any) => {
    const d = new Date(value)
    if (value === undefined) {
      return DEFAULT_PASS_VALUE
    } else if (d instanceof Date && !isNaN(d.getTime())) {
      return DEFAULT_PASS_VALUE
    }

    return error
  }

  date.type = VALIDATOR
  date.error = error

  return date
}
